import {
  makeStyles,
  InputAdornment,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _pick from 'lodash/pick';
import foundersAPI from '../../api/founders';
import { useFoundersContext } from '../../contexts/founders-context';
import { RouterContext } from '../../contexts/router-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { Link, Text, TextField } from '../common';
import List from '../common/list';
import FounderCard from '../list-cards/founder-card';

const useStyles = makeStyles(() => ({
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  searchInput: {
    width: '100%',
  },
  searchInputWrapper: {
    width: '100%',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
}));

export default function FoundersList() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { hasAccessToAction } = useContext(UserContext);
  const { prevPath } = useContext(RouterContext);
  const { founders, foundersLogos, setFounders } = useFoundersContext();

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const { page, search } = useMemo(() => {
    const parsedQuery = _pick(searchParams, ['page', 'search']);
    return {
      page: parsedQuery.page ? Number(parsedQuery.page) : 0,
      search: (parsedQuery.search as string) || undefined,
    };
  }, [searchParams]);

  const debouncedTimer = useRef<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchValue, setSearchValue] = useState(search || '');

  const prevFounderId = useMemo(() => {
    if (prevPath?.includes?.('/founders/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const foundersIndex = splitPath.indexOf('founders');
      const founderId = splitPath[foundersIndex + 1];

      if (founderId && founderId.length === 36) {
        return `card-${founderId}`;
      }
    } else if (prevPath?.includes?.('pfid=')) {
      const search = prevPath.split('?')[1];
      const query = parseQuery(search);
      if (query.pfid) {
        return `card-${query.pfid}`;
      }
    }
  }, [prevPath]);

  const prepareNewURL = useCallback(
    (nextSearchParams) => {
      return `${location.pathname}${
        Object.keys(nextSearchParams).length
          ? `?${stringifyQuery(nextSearchParams)}`
          : ''
      }`;
    },
    [location.pathname],
  );

  const handleSearchUpdate = useCallback(
    (search?: string) => {
      const clearSearchParams = {
        ...searchParams,
        page: 0,
        search,
      };
      history.replace(prepareNewURL(clearSearchParams));
    },
    [history, prepareNewURL, searchParams],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          handleSearchUpdate(e.target.value.trim());
        } else {
          handleSearchUpdate();
        }
      }, 800) as unknown as number;
    },
    [handleSearchUpdate],
  );

  const handleNextPage = useCallback(() => {
    if (!hasMorePages || isPageLoading) return;

    const nextSearchParams = {
      ...searchParams,
      page: page + 1,
    };
    history.replace(prepareNewURL(nextSearchParams));
  }, [hasMorePages, history, isPageLoading, page, prepareNewURL, searchParams]);

  const fetchFounders = useCallback(
    async ({ search, page = 0 }: { search?: string; page?: number }) => {
      try {
        if (search) {
          return await foundersAPI.search(search);
        } else {
          return await foundersAPI.getFoundersByStatus('Active', page);
        }
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.message === '404') {
          setHasMorePages(false);
          return [];
        }

        console.error(e);
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setLoadError(errorMessage);
        return [];
      }
    },
    [],
  );

  useEffect(() => {
    const loadFounders = async () => {
      setIsLoading(true);
      setFounders([]);

      if (!search && searchValue.length > 2) {
        setSearchValue('');
      }

      const pages = Array.from({ length: page + 1 }, (_, i) => i);
      const promises = pages.map((p) =>
        fetchFounders({
          search,
          page: p,
        }),
      );

      const loadedVenturesResult = await Promise.all(promises);
      const loadedVentures = loadedVenturesResult.flat();

      setFounders(loadedVentures);
      setIsLoading(false);
    };

    loadFounders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFounders, search]);

  useEffect(() => {
    const loadFoundersPage = async (page: number) => {
      if (isLoading) {
        return;
      }

      setIsPageLoading(true);

      const loadedFounders = await fetchFounders({
        page,
        search,
      });

      setFounders((prev) => [...prev, ...loadedFounders]);
      setIsPageLoading(false);
    };

    loadFoundersPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <List
      prevId={prevFounderId}
      loadError={loadError}
      isLoading={isLoading}
      isPageLoading={isPageLoading}
      hasMorePages={hasMorePages}
      onNextPage={!search ? handleNextPage : undefined}
      filter={
        <TextField
          className={cn(classes.searchInput, CLASS_TRACKING.FILTER_ELEMENT)}
          value={searchValue}
          onChange={handleSearch}
          placeholder='Search'
          disabled={isLoading || isPageLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          wrapperClassName={classes.searchInputWrapper}
          small
        />
      }>
      {!founders.length ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='founder-table-no-items'>
            This panel contains a list of founders
            {search ? ` with a keyword "${search}"` : ''}
            .<br />
            {hasAccessToAction('founder.create') &&
              'We were not able to find information you requested, but feel free to add something new!'}
          </Text>
          {hasAccessToAction('founder.create') && (
            <MaterialButton
              component={Link}
              to={Pages.NEW_FOUNDER}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              Add Founder
            </MaterialButton>
          )}
        </div>
      ) : (
        <div className={classes.listContainer} key={search}>
          {founders.map((founder) => (
            <FounderCard
              key={founder.id}
              founder={founder}
              logo={foundersLogos?.[founder.id]}
              cardId={`card-${founder.id}`}
            />
          ))}
        </div>
      )}
    </List>
  );
}
